/*
 * PaidHumanCard — v0.5 directive
 *
 * Quiet Solution-partner-help card. Appears when the agent emits the
 * `offer_paid_human` directive. Renders six mock slot pills (next three
 * weekdays × morning / afternoon). Clicking a slot flips the card to a
 * "Booked" confirmation. The Open-in-cal.com button is the real-booking
 * fallback — until cal.com slot API + webhook are wired, optimistic
 * confirmation is the contract.
 */

import React, { useMemo, useState } from 'react';
import { Calendar, Check, ExternalLink, ChevronLeft } from 'lucide-react';

import type { DirectivePayload } from '../../lib/dossier-types';


export interface PaidHumanBookSlot {
  slotId: string;
  dateLabel: string;
  timeLabel: string;
  consultantName?: string;
  consultantRole?: string;
}

export interface PaidHumanCardProps {
  payload: DirectivePayload;
  /** User clicked the cal.com fallback link ("See more times").
   *  Parent opens cal.com in a new tab. */
  onOpenCalendar: (url: string) => void;
  /** User picked a slot. Parent persists via /api/intake/paid-human/book
   *  and pushes a confirmation chat turn. Card waits for resolution
   *  before flipping to booked state, so a failure can roll back. */
  onBookSlot?: (slot: PaidHumanBookSlot) => Promise<void> | void;
  /** User dismissed the card. Parent should not show it again this turn
   *  unless scoping difficulty re-escalates. */
  onDismiss?: () => void;
  /** User wants to go back to the card they came from (the deep-scope fork).
   *  Parent removes this slot-picker turn so the fork is active again. When
   *  absent, the back affordance is hidden. */
  onBack?: () => void;
}


interface SlotOption {
  /** Stable id, e.g. 'tue-10' */
  id: string;
  /** "Tue, Jun 4" */
  dateLabel: string;
  /** "10:00 AM" */
  timeLabel: string;
}


function _initials(name?: string): string {
  if (!name) return 'AA';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || 'AA';
}


/** Generate six mock slots — next three weekdays, 10am + 2pm each.
 *  Skips weekends. Local-time labels using the user's locale. */
function _mockSlots(): SlotOption[] {
  const out: SlotOption[] = [];
  const now = new Date();
  let d = new Date(now);
  d.setHours(0, 0, 0, 0);
  let added = 0;
  while (added < 3) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    const dateLabel = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    for (const hour of [10, 14]) {
      const slotDate = new Date(d);
      slotDate.setHours(hour, 0, 0, 0);
      const timeLabel = slotDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      const dow3 = d.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      out.push({ id: `${dow3}-${hour}`, dateLabel, timeLabel });
    }
    added += 1;
  }
  return out;
}


export const PaidHumanCard: React.FC<PaidHumanCardProps> = ({
  payload, onOpenCalendar, onBookSlot, onDismiss, onBack,
}) => {
  const reason = payload.reason || "If it would help, you don't have to scope this alone.";
  // Slice 5 — short "why" chip that summarises the trigger (burned /
  // regulated / done-for-me / scoping stuck). Renders above the longer
  // reason text so the user catches the signal at a glance.
  const reasonLabel = payload.reason_label || null;
  const consultantName = payload.consultant_name || 'A Solution partner from our team';
  const consultantRole = payload.consultant_role || 'Healthcare practice lead';
  const slotUrl = payload.slot_picker_url || 'https://cal.com/team-brndsh/scoping';
  const expectations = payload.what_to_expect || [
    'Reads your scope before the call.',
    "30-minute session — you don't repeat anything.",
    'You walk out with a PRD and a workspace ready to build.',
  ];

  const slots = useMemo(_mockSlots, []);
  const [bookedSlot, setBookedSlot] = useState<SlotOption | null>(null);
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  const handlePickSlot = async (slot: SlotOption) => {
    if (booking || bookedSlot) return;
    setBookingError(null);
    setBooking(true);
    try {
      if (onBookSlot) {
        await onBookSlot({
          slotId: slot.id,
          dateLabel: slot.dateLabel,
          timeLabel: slot.timeLabel,
          consultantName,
          consultantRole,
        });
      }
      setBookedSlot(slot);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Booking failed';
      console.error('[PaidHumanCard] book failed', e);
      setBookingError(msg);
    } finally {
      setBooking(false);
    }
  };

  if (bookedSlot) {
    return (
      <div className="aa-directive aa-directive--paid-human aa-directive--paid-human-booked">
        <div className="aa-directive-ph-head">
          <div className="aa-directive-ph-avatar aa-directive-ph-avatar--booked">
            <Check size={20} strokeWidth={2.5} />
          </div>
          <div className="aa-directive-ph-titleblock">
            <div className="aa-directive-ph-eyebrow aa-directive-ph-eyebrow--booked">Session booked</div>
            <div className="aa-directive-ph-name">
              {bookedSlot.dateLabel} · {bookedSlot.timeLabel}
            </div>
            <div className="aa-directive-ph-role">
              <em>with {consultantName}</em>
            </div>
          </div>
        </div>

        <p className="aa-directive-ph-reason">
          Confirmation lands in your inbox. They'll read your scope before the call —
          no need to repeat anything.
        </p>

        <div className="aa-directive-actions">
          <button
            type="button"
            className="aa-directive-btn aa-directive-btn--ghost"
            onClick={() => onOpenCalendar(slotUrl)}
          >
            <Calendar size={13} strokeWidth={2.25} />
            View on cal.com
            <ExternalLink size={11} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-directive aa-directive--paid-human">
      {onBack && (
        <button type="button" className="aa-directive-ph-back" onClick={onBack}>
          <ChevronLeft size={13} strokeWidth={2.5} />
          Back
        </button>
      )}
      <div className="aa-directive-ph-head">
        <div className="aa-directive-ph-avatar">{_initials(consultantName)}</div>
        <div className="aa-directive-ph-titleblock">
          <div className="aa-directive-ph-eyebrow">Want a hand on this?</div>
          <div className="aa-directive-ph-name">{consultantName}</div>
          <div className="aa-directive-ph-role">
            <em>{consultantRole}</em>
          </div>
        </div>
      </div>

      {reasonLabel && (
        <div className="aa-directive-ph-reason-chip">
          <span className="aa-directive-ph-reason-chip-dot" aria-hidden="true" />
          <span>{reasonLabel}</span>
        </div>
      )}
      <p className="aa-directive-ph-reason">{reason}</p>

      {expectations.length > 0 && (
        <ul className="aa-directive-ph-expect">
          {expectations.map((e, i) => (
            <li key={i} className="aa-directive-ph-expect-row">
              <span className="aa-directive-ph-expect-dot" />
              <span>{e}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="aa-directive-ph-slots-label">Pick a time that works.</div>
      <div className="aa-directive-ph-slots">
        {slots.map((s) => (
          <button
            key={s.id}
            type="button"
            className="aa-directive-ph-slot"
            onClick={() => handlePickSlot(s)}
            disabled={booking}
          >
            <span className="aa-directive-ph-slot-date">{s.dateLabel}</span>
            <span className="aa-directive-ph-slot-time">
              {booking ? 'Booking…' : s.timeLabel}
            </span>
          </button>
        ))}
      </div>
      {bookingError && (
        <p className="aa-directive-ph-error">
          {bookingError}. Try again, or open the calendar below.
        </p>
      )}

      <div className="aa-directive-actions">
        <button
          type="button"
          className="aa-directive-btn aa-directive-btn--ghost"
          onClick={() => onOpenCalendar(slotUrl)}
        >
          <Calendar size={13} strokeWidth={2.25} />
          See more times
          <ExternalLink size={11} strokeWidth={2.5} />
        </button>
        {onDismiss && (
          <button
            type="button"
            className="aa-directive-btn aa-directive-btn--ghost"
            onClick={onDismiss}
          >
            Keep scoping with you
          </button>
        )}
      </div>
    </div>
  );
};

export default PaidHumanCard;
